import React, { useEffect, useState } from 'react';
import Seo from '../components/Seo';
import { Link } from 'react-router-dom';
import { Flame, MapPin, CalendarDays, Clock, Users, ArrowRight, Loader2, X } from 'lucide-react';
import { getEvents, rsvpEvent } from '../lib/apiClient';
import { useToast } from '../hooks/use-toast';
import Reveal from '../components/Reveal';

const fmtDate = (iso) => {
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (e) {
    return iso;
  }
};

const Events = () => {
  const { toast } = useToast();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // event selected for RSVP
  const [form, setForm] = useState({ name: '', email: '', guests: 1 });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getEvents()
      .then((data) => setEvents(data))
      .catch(() => toast({ title: 'Could not load events', description: 'Please try again shortly.' }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openRsvp = (ev) => {
    setActive(ev);
    setForm({ name: '', email: '', guests: 1 });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: 'Almost there', description: 'Please add your name and email.' });
      return;
    }
    setSubmitting(true);
    try {
      await rsvpEvent(active.id, { name: form.name, email: form.email, guests: Number(form.guests) || 1 });
      toast({ title: 'You’re on the list', description: `We saved your spot for ${active.title} in ${active.city}.` });
      setActive(null);
      try { setEvents(await getEvents()); } catch (e) { /* ignore */ }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({ title: detail ? 'Almost — one issue' : 'Something went wrong', description: detail || 'We could not save your RSVP. Please try again.' });
      try { setEvents(await getEvents()); } catch (e) { /* ignore */ }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-fire-ink">
      <Seo title="Events" path="/events" description="See upcoming After the Fire™ events and RSVP to experience testimony, music and rebuilding tools with Dr. Gee live." />
      {/* Hero with Dr. Gee photos in the background */}
      <section className="pt-32 pb-16 text-center relative overflow-hidden">
        {/* Background photos */}
        <div className="absolute inset-0 grid grid-cols-2" data-testid="event-hero-bg">
          <div className="bg-cover [background-position:center_20%] md:[background-position:center_top]" style={{ backgroundImage: 'url(/brand/drgee-travel-2.jpg)' }} />
          <div className="bg-cover [background-position:center_20%] md:[background-position:center_top]" style={{ backgroundImage: 'url(/brand/drgee-travel-1.jpg)' }} />
        </div>
        {/* Dark overlay for readability */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(23,17,13,0.82) 0%, rgba(23,17,13,0.72) 50%, rgba(23,17,13,0.95) 100%)' }} />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full opacity-20 blur-3xl" style={{ background: 'radial-gradient(circle, rgba(217,117,36,0.4), transparent 60%)' }} />
        <div className="max-w-3xl mx-auto px-6 relative">
          <Flame className="w-9 h-9 text-fire-gold mx-auto mb-5 animate-flicker" strokeWidth={1.4} />
          <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">FIND AN ATF EXPERIENCE</span>
          <h1 className="font-display text-4xl md:text-6xl text-fire-cream mt-4">Come This Way</h1>
          <p className="text-fire-cream/70 font-body mt-4 max-w-xl mx-auto">
            Find an After the Fire™ Experience near you — reserve your seat in a room full of women who have survived their own fires.
          </p>
        </div>
      </section>

      {/* Events list */}
      <section className="py-16">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-10 h-10 text-fire-gold animate-spin" />
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-cormorant text-2xl text-fire-cream/70">No events scheduled just yet.</p>
              <p className="text-fire-cream/50 font-body mt-2">Check back soon — or bring ATF to your city.</p>
              <Link to="/book" className="btn-gold rounded-full px-7 py-3.5 font-semibold inline-flex items-center gap-2 text-sm mt-6">
                Bring ATF to My City <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="space-y-5">
              {events.map((ev, i) => (
                <Reveal key={ev.id} delay={(i % 4) * 60}>
                  <div className="card-dark rounded-2xl p-6 md:p-8 hover-lift flex flex-col md:flex-row md:items-center gap-6">
                    {/* Date block */}
                    <div className="shrink-0 w-full md:w-28 text-center bg-gradient-to-b from-fire-maroon to-fire-maroondark rounded-xl py-4 border border-fire-gold/25">
                      <p className="font-display text-3xl text-fire-cream leading-none">{new Date(ev.date + 'T00:00:00').getDate()}</p>
                      <p className="font-marcellus tracking-widest text-fire-goldlight text-xs mt-1">
                        {new Date(ev.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}
                      </p>
                      <p className="font-body text-fire-cream/50 text-[11px] mt-0.5">{new Date(ev.date + 'T00:00:00').getFullYear()}</p>
                    </div>
                    {/* Details */}
                    <div className="flex-1">
                      <span className="inline-block bg-fire-ink/70 border border-fire-gold/25 text-fire-goldlight text-[10px] font-body tracking-wide px-3 py-1 rounded-full mb-2">{ev.type}</span>
                      <h3 className="font-display text-2xl text-fire-cream">{ev.title}</h3>
                      <p className="text-fire-cream/60 font-body text-sm mt-2 leading-relaxed max-w-2xl">{ev.description}</p>
                      <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-fire-cream/70 font-body text-sm">
                        <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-fire-gold" /> {ev.city}{ev.venue ? ` · ${ev.venue}` : ''}</span>
                        <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-fire-gold" /> {fmtDate(ev.date)}</span>
                        {ev.time && <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-fire-gold" /> {ev.time}</span>}
                        {ev.spots ? <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4 text-fire-gold" /> {ev.is_full ? 'Sold out' : `${ev.seats_left} of ${ev.spots} seats left`}</span> : null}
                      </div>
                    </div>
                    {/* CTA */}
                    <div className="shrink-0">
                      {ev.is_full ? (
                        <span className="inline-flex items-center gap-2 rounded-full px-7 py-3 font-semibold text-sm border border-fire-maroon/60 text-fire-cream/60 whitespace-nowrap">
                          Sold Out
                        </span>
                      ) : (
                        <button onClick={() => openRsvp(ev)} className="btn-gold rounded-full px-7 py-3 font-semibold text-sm inline-flex items-center gap-2 whitespace-nowrap">
                          Reserve a Seat
                        </button>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-fire-maroondark via-fire-ink to-fire-maroondark" />
        <div className="relative max-w-[800px] mx-auto px-6 text-center">
          <Flame className="w-9 h-9 text-fire-gold mx-auto mb-5 animate-flicker" strokeWidth={1.4} />
          <h2 className="font-display text-3xl md:text-4xl text-fire-cream mb-4">Don’t see your city?</h2>
          <p className="text-fire-cream/70 font-body mb-8 max-w-xl mx-auto">Bring the After the Fire™ Experience to your church, conference or organization.</p>
          <Link to="/book" className="btn-gold rounded-full px-8 py-4 font-semibold inline-flex items-center gap-2">
            Bring ATF to My City <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* RSVP modal */}
      {active && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center px-5">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setActive(null)} />
          <div className="relative card-dark rounded-2xl p-7 md:p-9 max-w-md w-full animate-fade-up">
            <button onClick={() => setActive(null)} className="absolute top-4 right-4 text-fire-cream/50 hover:text-fire-goldlight">
              <X className="w-5 h-5" />
            </button>
            <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">RESERVE A SEAT</span>
            <h3 className="font-display text-2xl text-fire-cream mt-2 mb-1">{active.title}</h3>
            <p className="text-fire-cream/60 font-body text-sm mb-6">{active.city} · {fmtDate(active.date)}</p>
            <form onSubmit={submit} className="space-y-4">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="w-full bg-fire-ink/70 border border-fire-gold/25 rounded-lg px-4 py-3 text-fire-cream placeholder:text-fire-cream/35 focus:outline-none focus:border-fire-gold transition-colors" />
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email address" className="w-full bg-fire-ink/70 border border-fire-gold/25 rounded-lg px-4 py-3 text-fire-cream placeholder:text-fire-cream/35 focus:outline-none focus:border-fire-gold transition-colors" />
              <div>
                <label className="block font-body text-sm text-fire-cream/70 mb-2">Number of guests</label>
                <input type="number" min={1} max={20} value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} className="w-full bg-fire-ink/70 border border-fire-gold/25 rounded-lg px-4 py-3 text-fire-cream focus:outline-none focus:border-fire-gold transition-colors" />
              </div>
              <button type="submit" disabled={submitting} className="btn-gold w-full rounded-full py-3.5 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70">
                {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>) : 'Confirm My Seat'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;
